import dagre from '@dagrejs/dagre';
import { getCustomNodeOptions, getCustomTypeName } from '@/flow/flow/utils/custom-types';
import type { Flow, Node, Transition, Trigger } from '@keyvue/one-api-client';

const NODE_WIDTH = 512;
const NODE_HEIGHT = 96;
const HORIZONTAL_SPACING = NODE_WIDTH * 0.5;
const VERTICAL_SPACING = NODE_HEIGHT;

export default function useGraphGenerator() {
    const graph = new dagre.graphlib.Graph();
    graph.setDefaultEdgeLabel(() => ({}));

    graph.setGraph({
        nodesep: HORIZONTAL_SPACING,
        ranksep: VERTICAL_SPACING,
    });

    function fakePadding(content: string) {
        // eslint-disable-next-line no-irregular-whitespace
        return `    ${content}    `;
    }

    function generateLabelForEdge(transition: Transition) {
        switch (transition.condition) {
            case null: return null;
            case 'true': return fakePadding('JA');
            case 'false': return fakePadding('NEE');
            default: return fakePadding(transition.condition);
        }
    }

    /**
     * Updates the graph with the nodes and edges from the flow.
     * This has to be done before generating the node data.
     */
    function updateGraph(flow: Flow) {
        // Set triggers, they all point to startNode
        flow.triggers.forEach((trigger) => {
            graph.setNode(trigger.id.value, {
                width: NODE_WIDTH,
                height: NODE_HEIGHT,
            });

            graph.setEdge(trigger.id.value, flow.startNode.id.value);
        });

        // Sort nodes to render nodes with a left-positioned target to the right
        flow.nodes.sort((a, b) => {
            const aCustomTypeName = getCustomTypeName(a);
            const bCustomTypeName = getCustomTypeName(b);

            if (['node_cancel'].includes(aCustomTypeName)) {
                return 1;
            }

            if (['node_cancel'].includes(bCustomTypeName)) {
                return -1;
            }

            return 0;
        });

        // Set nodes
        flow.nodes.forEach((node) => {
            graph.setNode(node.id.value, {
                width: NODE_WIDTH,
                height: NODE_HEIGHT,
            });
        });

        // Generate edges
        flow.transitions.forEach((transition) => {
            graph.setEdge(transition.from.id.value, transition.to.id.value);
        });

        // Generate layout
        dagre.layout(graph);

        // Adjust layout
        graph.nodes().forEach((node) => {
            const flowNode = flow.nodes.find((n) => n.id.value === node);
            const trigger = flow.triggers.find((t) => t.id.value === node);
            const triggerOrNode = trigger || flowNode;

            if (!triggerOrNode) {
                return;
            }

            const graphNode = graph.node(node);
            if (!graphNode) {
                return;
            }

            const customTypeName = getCustomTypeName(triggerOrNode);

            // Move nodes with a left-positioned target upwards so that horizontal edge is straight
            if (['node_cancel'].includes(customTypeName)) {
                graphNode.x += (HORIZONTAL_SPACING / 2);
                graphNode.y -= (NODE_HEIGHT / 2 + VERTICAL_SPACING / 2);
            }

            // Move nodes with "full" variant leftwards so that vertical edge is straight
            if (['node_end'].includes(customTypeName) || trigger) {
                graphNode.x -= (NODE_WIDTH / 2 - NODE_HEIGHT / 2);
            }
        });
    }

    /**
     * Generates the node data for a trigger or node.
     */
    function generateNode(triggerOrNode: Trigger | Node) {
        const customTypeName = getCustomTypeName(triggerOrNode);
        const customTypeOptions = getCustomNodeOptions(customTypeName);

        return {
            id: triggerOrNode.id.value,
            label: customTypeName,
            type: customTypeName,
            data: triggerOrNode,
            position: {
                x: graph.node(triggerOrNode.id.value).x,
                y: graph.node(triggerOrNode.id.value).y,
            },
            ...customTypeOptions,
        };
    }

    function generateEdge(source: string, target: string, options: any = {}) {
        return {
            id: `${source}-${target}`,
            source,
            target,
            animated: false,
            type: 'step',
            position: {
                x: graph.edge({ v: source, w: target }).points[1].x,
                y: graph.edge({ v: source, w: target }).points[1].y,
            },
            ...options,
        };
    }

    function generateFlowData(flow: Flow) {
        updateGraph(flow);

        return [
            ...flow.triggers.map(generateNode),
            ...flow.nodes.map(generateNode),
            ...flow.transitions.map((transition) => generateEdge(
                transition.from.id.value,
                transition.to.id.value,
                {
                    label: generateLabelForEdge(transition),
                },
            )),
            ...flow.triggers.map((trigger) => generateEdge(
                trigger.id.value,
                flow.startNode.id.value,
                {
                    // animated: true,
                },
            )),
        ];
    }

    return {
        generateFlowData,
    };
}
